/**
 * Canonical review statuses defined for H1 Bloc A (ADR – statuses/DSL bridge).
 * Intended as the shared vocabulary for human reviews on requirements, spec sections, etc.
 */
export const REVIEW_STATUS = {
  draft: "draft",
  pending_review: "pending_review",
  in_review: "in_review",
  approved: "approved",
  rejected: "rejected",
  archived: "archived",
} as const;

export type ReviewStatus = (typeof REVIEW_STATUS)[keyof typeof REVIEW_STATUS];

/**
 * Helper list of {@link ReviewStatus} values.
 */
export const REVIEW_STATUSES: ReviewStatus[] = Object.values(REVIEW_STATUS);

/**
 * Canonical AI review statuses defined for H1 Bloc A (ADR – statuses/DSL bridge).
 * Intended for machine-led review workflows on requirements and spec sections.
 */
export const AI_REVIEW_STATUS = {
  none: "none",
  pending: "pending",
  in_progress: "in_progress",
  completed: "completed",
  rejected: "rejected",
} as const;

/**
 * Canonical AI review statuses defined for H1 Bloc A (ADR – statuses/DSL bridge).
 * Intended for machine-led review workflows on requirements and spec sections.
 */
export type AiReviewStatus = (typeof AI_REVIEW_STATUS)[keyof typeof AI_REVIEW_STATUS];

/**
 * Helper list of {@link AiReviewStatus} values.
 */
export const AI_REVIEW_STATUSES: AiReviewStatus[] = Object.values(AI_REVIEW_STATUS);

/**
 * Canonical execution statuses defined for H1 Bloc A (ADR – statuses/DSL bridge).
 * Intended for workplan, work item, and checklist execution tracking.
 */
export const PRISMA_EXECUTION_STATUS = {
  notRequested: "not_requested",
  pending: "pending",
  notPlanned: "not_planned",
  planned: "planned",
  inProgress: "in_progress",
  blocked: "blocked",
  completed: "completed",
  done: "done",
  failed: "failed",
  dropped: "dropped",
} as const;

export type PrismaExecutionStatus =
  (typeof PRISMA_EXECUTION_STATUS)[keyof typeof PRISMA_EXECUTION_STATUS];

export type ExecutionStatus = PrismaExecutionStatus;

/**
 * Helper list of {@link ExecutionStatus} values.
 */
export const PRISMA_EXECUTION_STATUSES: PrismaExecutionStatus[] = Object.values(PRISMA_EXECUTION_STATUS);
export const EXECUTION_STATUSES: ExecutionStatus[] = PRISMA_EXECUTION_STATUSES;

export const EXECUTION_STATUS = {
  ...PRISMA_EXECUTION_STATUS,
  /**
   * Legacy/UI alias preserved for compatibility. Maps to the Prisma `planned` value.
   */
  notStarted: PRISMA_EXECUTION_STATUS.planned,
  /**
   * Legacy/UI alias preserved for compatibility. Maps to the Prisma `dropped` value.
   */
  cancelled: PRISMA_EXECUTION_STATUS.dropped,
} as const satisfies Record<string, PrismaExecutionStatus>;

const EXECUTION_STATUS_ALIASES: Record<string, ExecutionStatus> = {
  not_requested: PRISMA_EXECUTION_STATUS.notRequested,
  notrequested: PRISMA_EXECUTION_STATUS.notRequested,
  pending: PRISMA_EXECUTION_STATUS.pending,
  not_planned: PRISMA_EXECUTION_STATUS.notPlanned,
  notplanned: PRISMA_EXECUTION_STATUS.notPlanned,
  planned: PRISMA_EXECUTION_STATUS.planned,
  notstarted: PRISMA_EXECUTION_STATUS.planned,
  "not started": PRISMA_EXECUTION_STATUS.planned,
  not_started: PRISMA_EXECUTION_STATUS.planned,
  draft: PRISMA_EXECUTION_STATUS.planned,
  inprogress: PRISMA_EXECUTION_STATUS.inProgress,
  "in progress": PRISMA_EXECUTION_STATUS.inProgress,
  completed: PRISMA_EXECUTION_STATUS.completed,
  failed: PRISMA_EXECUTION_STATUS.failed,
  dropped: PRISMA_EXECUTION_STATUS.dropped,
  cancelled: PRISMA_EXECUTION_STATUS.dropped,
  canceled: PRISMA_EXECUTION_STATUS.dropped,
};

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

export function isExecutionStatus(value: unknown): value is ExecutionStatus {
  return parseExecutionStatus(value) !== null;
}

export function parseExecutionStatus(
  value: unknown,
  fallback?: ExecutionStatus,
): ExecutionStatus | null {
  if (typeof value !== "string") return fallback ?? null;

  const normalized = normalizeKey(value);

  if ((EXECUTION_STATUSES as readonly string[]).includes(normalized)) {
    return normalized as ExecutionStatus;
  }

  const alias = EXECUTION_STATUS_ALIASES[normalized.replace(/[-\s]/g, "_")];
  if (alias) return alias;

  return fallback ?? null;
}

export function isPrismaExecutionStatus(value: unknown): value is PrismaExecutionStatus {
  return toPrismaExecutionStatus(value) !== null;
}

export function toPrismaExecutionStatus(
  value: unknown,
  fallback?: PrismaExecutionStatus,
): PrismaExecutionStatus | null {
  const normalized = parseExecutionStatus(value);
  if (normalized) return normalized;

  return fallback ?? null;
}

export const REQUIREMENT_REVIEW_STATUSES = REVIEW_STATUSES;
export type RequirementReviewStatus = ReviewStatus;

export const EXISTING_AI_REVIEW_STATUSES = [
  "none",
  "pending",
  "in_progress",
  "completed",
  "rejected",
] as const;
export type ExistingAiReviewStatus = (typeof EXISTING_AI_REVIEW_STATUSES)[number];

/**
 * Canonical statuses for requirement generation workflows.
 * Aligns with database enums and migrations.
 */
export type RequirementGenerationStatus = ExecutionStatus;

export const REQUIREMENT_GENERATION_STATUS = {
  not_requested: PRISMA_EXECUTION_STATUS.notRequested,
  pending: PRISMA_EXECUTION_STATUS.pending,
  not_planned: PRISMA_EXECUTION_STATUS.notPlanned,
  planned: PRISMA_EXECUTION_STATUS.planned,
  in_progress: PRISMA_EXECUTION_STATUS.inProgress,
  blocked: PRISMA_EXECUTION_STATUS.blocked,
  completed: PRISMA_EXECUTION_STATUS.completed,
  done: PRISMA_EXECUTION_STATUS.done,
  failed: PRISMA_EXECUTION_STATUS.failed,
  dropped: PRISMA_EXECUTION_STATUS.dropped,
} as const satisfies Record<string, RequirementGenerationStatus>;

export const REQUIREMENT_GENERATION_STATUSES: RequirementGenerationStatus[] = Object.values(
  REQUIREMENT_GENERATION_STATUS,
);

export const REQUIREMENT_GENERATION_TARGETS = [
  "backend_module",
  "frontend_module",
  "full_stack_module",
] as const;
export type RequirementGenerationTarget = (typeof REQUIREMENT_GENERATION_TARGETS)[number];

export const SPEC_SECTION_DSL_KINDS = [
  "DOMAIN",
  "UI",
  "UI_FIELD",
  "WORKFLOW",
  "WORKFLOW_STATE",
  "PERMISSION",
  "OTHER",
] as const;
export type SpecSectionDslKind = (typeof SPEC_SECTION_DSL_KINDS)[number];

export const SPEC_SECTION_DSL_STATUS = {
  NOT_STARTED: "NOT_STARTED",
  CANDIDATE: "CANDIDATE",
  ACCEPTED: "ACCEPTED",
  OUTDATED: "OUTDATED",
} as const;

export type SpecSectionDslStatus = (typeof SPEC_SECTION_DSL_STATUS)[keyof typeof SPEC_SECTION_DSL_STATUS];

export const SPEC_SECTION_DSL_STATUSES: SpecSectionDslStatus[] = Object.values(SPEC_SECTION_DSL_STATUS);

export const WORK_STATUSES = ["not_started", "in_progress", "blocked", "done", "cancelled"] as const;
export type WorkStatus = (typeof WORK_STATUSES)[number];

export const PROJECT_HEALTH_STATUSES = ["on_track", "at_risk", "off_track"] as const;
export type ProjectHealthStatus = (typeof PROJECT_HEALTH_STATUSES)[number];
